import React, { useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Flex,
  Badge,
  Progress,
  Icon,
  Grid,
  GridItem,
  HStack,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { FiUsers, FiTrendingUp, FiInfo, FiChevronsRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

// Mock investment options with votes
const INVESTMENT_OPTIONS = [
  {
    id: "property",
    title: "Long-term Property Growth",
    description: "A 5-year property investment strategy focusing on capital growth in high-potential areas.",
    votes: 37,
    color: "blue.500"
  },
  {
    id: "business",
    title: "Business Investment",
    description: "Investing in small to medium businesses with sustainable practices, like regenerative agriculture.",
    votes: 28,
    color: "purple.500"
  },
  {
    id: "token",
    title: "Tokenized Real Estate",
    description: "Fractional ownership through blockchain tokens, providing liquidity and smaller investment threshold.",
    votes: 42,
    color: "green.500"
  },
  {
    id: "rental",
    title: "Income-Focused Strategy",
    description: "Focus on property with strong rental yields and consistent income generation.",
    votes: 19,
    color: "orange.500"
  }
];

const InvestmentResults = () => {
  const navigate = useNavigate();
  const bgGradient = useColorModeValue(
    "linear-gradient(135deg, rgba(44, 82, 130, 0.05) 0%, rgba(57, 161, 105, 0.1) 100%)",
    "linear-gradient(135deg, rgba(44, 82, 130, 0.2) 0%, rgba(57, 161, 105, 0.3) 100%)"
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Calculate total votes
  const totalVotes = INVESTMENT_OPTIONS.reduce((sum, option) => sum + option.votes, 0);
  
  // Sort options by votes in descending order
  const sortedOptions = [...INVESTMENT_OPTIONS].sort((a, b) => b.votes - a.votes);
  const leadingOption = sortedOptions[0];

  return (
    <Box bg="#f7f7f7" minH="100vh">
      <NavbarWithCallToAction />
      
      {/* Hero Section */}
      <Box 
        bg="linear-gradient(135deg, #2c5282 0%, #39a169 100%)" 
        py={16} 
        color="white"
      >
        <Container maxW="container.md">
          <VStack spacing={6} textAlign="center">
            <Badge
              px={4}
              py={2}
              bg="white"
              color="#2c5282"
              borderRadius="full"
              fontSize="md"
              fontWeight="600"
            >
              POLL RESULTS
            </Badge>
            
            <Heading size="2xl" lineHeight="1.2">
              Investment Strategy Preferences
            </Heading>
            
            <Text fontSize="xl" maxW="700px">
              See how our community is voting on different investment strategies
              and help shape the future of our blockchain-powered property platform
            </Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.lg" py={16}>
        <VStack spacing={12}>
          {/* Leading Option Showcase */}
          <Box 
            w="full" 
            bg="white" 
            p={8} 
            borderRadius="xl" 
            boxShadow="md"
            border="1px solid"
            borderColor="gray.100"
            position="relative"
            overflow="hidden"
          >
            {/* Background decoration */}
            <Box
              position="absolute"
              top="-50px"
              right="-50px"
              width="200px"
              height="200px"
              bg="green.50"
              borderRadius="full"
              zIndex={0}
            />
            
            <VStack spacing={6} align="start" w="full" position="relative" zIndex={1}>
              <Badge colorScheme="green" fontSize="md" py={1} px={3}>
                Leading Choice
              </Badge>
              
              <Heading size="xl" color={leadingOption.color}>
                {leadingOption.title}
              </Heading>
              
              <Text fontSize="lg">
                {leadingOption.description}
              </Text>
              
              <Flex w="full" align="center" gap={4}>
                <Progress 
                  value={(leadingOption.votes / totalVotes) * 100} 
                  colorScheme="green" 
                  size="lg" 
                  borderRadius="full"
                  flex="1"
                />
                <Text fontWeight="bold" fontSize="xl">
                  {Math.round((leadingOption.votes / totalVotes) * 100)}%
                </Text>
              </Flex>
              
              <HStack spacing={4}>
                <Badge colorScheme="blue" py={1} px={3} borderRadius="full">
                  <Flex align="center" gap={1}>
                    <Icon as={FiUsers} />
                    <Text>{leadingOption.votes} votes</Text>
                  </Flex>
                </Badge>
                
                <Badge colorScheme="green" py={1} px={3} borderRadius="full">
                  <Flex align="center" gap={1}>
                    <Icon as={FiTrendingUp} />
                    <Text>Current leader</Text>
                  </Flex>
                </Badge>
              </HStack>
            </VStack>
          </Box>

          {/* All Results */}
          <Box w="full">
            <Heading size="lg" mb={6}>Complete Results</Heading>
            
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
              {sortedOptions.map((option, index) => (
                <GridItem key={option.id}>
                  <Box 
                    p={6} 
                    borderRadius="lg" 
                    bg="white" 
                    boxShadow="sm"
                    border="1px solid"
                    borderColor="gray.100"
                    position="relative"
                  >
                    {index === 0 && (
                      <Badge 
                        position="absolute" 
                        top="-10px" 
                        right="10px"
                        colorScheme="green"
                        px={2}
                      >
                        Winner
                      </Badge>
                    )}
                    
                    <VStack align="start" spacing={3}>
                      <Heading size="md" color={option.color}>{option.title}</Heading>
                      <Text fontSize="sm" color="gray.600">{option.description}</Text>
                      
                      <Box w="full" pt={2}>
                        <Flex justify="space-between" mb={1}>
                          <Text fontSize="sm" fontWeight="bold">{option.votes} votes</Text>
                          <Text fontSize="sm" fontWeight="bold">
                            {Math.round((option.votes / totalVotes) * 100)}%
                          </Text>
                        </Flex>
                        <Progress 
                          value={(option.votes / totalVotes) * 100} 
                          colorScheme={option.color.split(".")[0]} 
                          size="md" 
                          borderRadius="full" 
                        />
                      </Box>
                    </VStack>
                  </Box>
                </GridItem>
              ))}
            </Grid>
          </Box>

          <Divider />

          {/* Next Steps Section */}
          <Box 
            w="full" 
            p={8} 
            borderRadius="xl"
            bg={bgGradient}
          >
            <VStack spacing={8}>
              <Heading size="lg" textAlign="center">What Happens Next?</Heading>
              
              <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                <GridItem>
                  <VStack spacing={4} align="start" h="full">
                    <Flex 
                      w="50px" 
                      h="50px" 
                      bg="blue.500" 
                      color="white" 
                      borderRadius="full"
                      align="center"
                      justify="center"
                      fontSize="xl"
                      fontWeight="bold"
                    >
                      1
                    </Flex>
                    <Heading size="md">Results Analysis</Heading>
                    <Text>Our team will review the community&apos;s votes and preferences to shape our investment approach.</Text>
                  </VStack>
                </GridItem>
                
                <GridItem>
                  <VStack spacing={4} align="start" h="full">
                    <Flex 
                      w="50px" 
                      h="50px" 
                      bg="purple.500" 
                      color="white" 
                      borderRadius="full"
                      align="center"
                      justify="center"
                      fontSize="xl"
                      fontWeight="bold"
                    >
                      2
                    </Flex>
                    <Heading size="md">Proposal Development</Heading>
                    <Text>We&apos;ll develop a detailed investment proposal based on the winning strategy.</Text>
                  </VStack>
                </GridItem>
                
                <GridItem>
                  <VStack spacing={4} align="start" h="full">
                    <Flex 
                      w="50px" 
                      h="50px" 
                      bg="green.500" 
                      color="white" 
                      borderRadius="full"
                      align="center"
                      justify="center"
                      fontSize="xl"
                      fontWeight="bold"
                    >
                      3
                    </Flex>
                    <Heading size="md">Member Opportunity</Heading>
                    <Text>Life Essentials Club members will have first access to participate in the investment opportunity.</Text>
                  </VStack>
                </GridItem>
              </Grid>
              
              <Box w="full" textAlign="center" pt={6}>
                <Button
                  colorScheme="green"
                  size="lg"
                  rightIcon={<Icon as={FiChevronsRight} />}
                  onClick={() => navigate("/join")}
                  px={8}
                >
                  Join the Club for $1
                </Button>
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Secure your place in our investment community
                </Text>
              </Box>
            </VStack>
          </Box>

          {/* Info Section */}
          <Box 
            w="full" 
            bg="white" 
            p={8} 
            borderRadius="xl" 
            boxShadow="md"
            border="1px solid"
            borderColor="gray.100"
          >
            <Flex direction={{ base: "column", md: "row" }} gap={6} align="center">
              <Icon as={FiInfo} w={10} h={10} color="blue.500" />
              <Box>
                <Heading size="md" mb={2}>Stay Updated</Heading>
                <Text color="gray.600">
                  We&apos;ll be sending updates on the selected investment strategy and next steps to all members.
                  Make sure you&apos;re subscribed to our podcast and join the club to stay informed and participate in this exciting opportunity.
                </Text>
              </Box>
              <Button
                colorScheme="blue"
                size="md"
                rightIcon={<ChevronRightIcon />}
                onClick={() => navigate("/p1")}
                flexShrink={0}
              >
                Listen to Episode 1
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>
      
      <FooterWithFourColumns />
    </Box>
  );
};

export default InvestmentResults; 